import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  Box,
  Button,
  Container,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { getCurrentUser, updatePassword } from 'aws-amplify/auth';
import { FormikHelpers, useFormik } from 'formik';
import * as React from 'react';
import { toast } from 'react-toastify';
import * as Yup from 'yup';

import useMixPanel from '@/hooks/useMixPanel';

interface FormValues {
  oldPassword: string;
  newPassword: string;
  newConfirmPassword: string;
}

const validationSchema = Yup.object().shape({
  oldPassword: Yup.string().required('Old Password is required'),
  newPassword: Yup.string().required('New Password is required'),
  newConfirmPassword: Yup.string()
    .required('Confirm Password is required')
    .oneOf([Yup.ref('newPassword'), null], 'Passwords must match'),
});

const FormInputWrapper = styled(Box)({
  paddingTop: 8,
  paddingBottom: 8,
});

const ResetPasswordPage = () => {
  const { trackEvent } = useMixPanel();
  const [showOldPassword, setShowOldPassword] = React.useState(false);
  const [showNewPassword, setShowNewPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);

  const handleSubmit = async (values: FormValues, { resetForm }: FormikHelpers<FormValues>) => {
    getCurrentUser()
      .then(async (user) => {
        const res = await updatePassword({
          oldPassword: values.oldPassword,
          newPassword: values.newPassword,
        });
        trackEvent('User Password Reset', {
          UserID: user.userId,
        });
        toast.success('Password updated successfully!', { closeButton: true });
        return res;
      })
      .catch((err) => toast.error(`Error: ${err.message}`, { closeButton: true }))
      .finally(() => resetForm());
  };
  const formik = useFormik({
    initialValues: {
      oldPassword: '',
      newPassword: '',
      newConfirmPassword: '',
    },
    onSubmit: handleSubmit,
    validationSchema,
    validateOnChange: false,
    validateOnBlur: false,
  });

  return (
    <Container>
      <form onSubmit={formik.handleSubmit}>
        <h4 style={{ textAlign: 'center' }}>Reset Password</h4>
        <Stack alignItems="center">
          <FormInputWrapper>
            <TextField
              label="Old Password"
              name="oldPassword"
              type={showOldPassword ? 'text' : 'password'}
              value={formik.values.oldPassword}
              onChange={formik.handleChange}
              variant="outlined"
              error={!!formik.errors.oldPassword}
              sx={{
                width: 300,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '14px',
                  minHeight: '45px',
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle old password visibility"
                      onClick={() => setShowOldPassword(!showOldPassword)}
                      edge="end"
                    >
                      {showOldPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {formik.errors.oldPassword && (
              <Typography
                fontSize="16px"
                fontWeight={400}
                variant="body2"
                sx={{ color: '#e53935' }}
              >
                {formik.errors.oldPassword}
              </Typography>
            )}
          </FormInputWrapper>
          <FormInputWrapper>
            <TextField
              label="New Password"
              name="newPassword"
              type={showNewPassword ? 'text' : 'password'}
              value={formik.values.newPassword}
              onChange={formik.handleChange}
              variant="outlined"
              error={!!formik.errors.newPassword}
              sx={{
                width: 300,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '14px',
                  minHeight: '45px',
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle new password visibility"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      edge="end"
                    >
                      {showNewPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {formik.errors.newPassword && (
              <Typography
                fontSize="16px"
                fontWeight={400}
                variant="body2"
                sx={{ color: '#e53935' }}
              >
                {formik.errors.newPassword}
              </Typography>
            )}
          </FormInputWrapper>
          <FormInputWrapper>
            <TextField
              label="Confirm New Password"
              name="newConfirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              value={formik.values.newConfirmPassword}
              onChange={formik.handleChange}
              variant="outlined"
              error={!!formik.errors.newConfirmPassword}
              sx={{
                width: 300,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '14px',
                  minHeight: '45px',
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle confirm password visibility"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {formik.errors.newConfirmPassword && (
              <Typography
                fontSize="16px"
                fontWeight={400}
                variant="body2"
                sx={{ color: '#e53935' }}
              >
                {formik.errors.newConfirmPassword}
              </Typography>
            )}
          </FormInputWrapper>
          <Button
            type="submit"
            sx={{ mt: 2, width: 300, height: 45, borderRadius: '14px' }}
            variant="contained"
          >
            Submit
          </Button>
        </Stack>
      </form>
    </Container>
  );
};

export default ResetPasswordPage;
